import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingCart } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { formatPrice, calculateDiscount } from '../../utils/helpers';

const ProductCard = ({ product }) => {
  const { addToCart, addToWishlist, removeFromWishlist, isInWishlist } = useApp();
  const [addingToCart, setAddingToCart] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);

  const discount = calculateDiscount(product.original_price, product.price);
  const inWishlist = isInWishlist(product.id);
  const hasMultipleImages = product.images && product.images.length > 1;

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setAddingToCart(true);
    const success = await addToCart(product.id, 1);
    if (success) {
      setTimeout(() => setAddingToCart(false), 500);
    } else {
      setAddingToCart(false);
    }
  };

  const handleToggleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (inWishlist) {
      await removeFromWishlist(product.id);
    } else {
      await addToWishlist(product.id);
    }
  };

  return (
    <Link
      to={`/product/${product.id}`}
      className="group relative block overflow-hidden aspect-[3/4]"
      onMouseEnter={() => hasMultipleImages && setImageIndex(1)}
      onMouseLeave={() => setImageIndex(0)}
      data-testid={`product-card-${product.id}`}
    >
      {/* Full-bleed product image */}
      <img
        src={product.images[imageIndex] || product.images[0] || 'https://via.placeholder.com/400'}
        alt={product.name}
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
      />

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#3E2723]/70 via-[#3E2723]/15 to-transparent" />

      {/* Discount badge */}
      {discount > 0 && (
        <div className="absolute top-4 left-4 bg-[#FEFCF9] text-[#3E2723] px-3 py-1 text-xs font-semibold tracking-wider uppercase">
          {discount}% Off
        </div>
      )}

      {/* Wishlist button */}
      <button
        onClick={handleToggleWishlist}
        className={`absolute top-4 right-4 p-2.5 rounded-full transition-all duration-300 z-10 ${
          inWishlist
            ? 'bg-red-50 shadow-lg'
            : 'bg-[#FEFCF9]/90 backdrop-blur-sm hover:bg-white shadow-md'
        }`}
        data-testid={`wishlist-button-${product.id}`}
      >
        <Heart
          className={`w-4 h-4 transition-colors ${
            inWishlist ? 'fill-red-500 text-red-500' : 'text-[#8B7D6B] hover:text-[#C4A265]'
          }`}
        />
      </button>

      {/* Bottom text overlay */}
      <div className="absolute inset-x-0 bottom-0 p-3 sm:p-4 lg:p-6 text-white">
        <span className="text-[8px] sm:text-[10px] tracking-[0.2em] text-white/60 uppercase font-medium">
          {product.craft_type}
        </span>
        <h3 className="font-serif text-sm sm:text-base lg:text-lg leading-snug mt-0.5 sm:mt-1 mb-1 sm:mb-2 line-clamp-2">
          {product.name}
        </h3>
        <div className="flex items-center justify-between">
          <div className="flex items-baseline gap-1 sm:gap-2">
            <span className="text-sm sm:text-base lg:text-lg font-semibold">{formatPrice(product.price)}</span>
            {product.original_price && product.original_price > product.price && (
              <span className="text-[10px] sm:text-sm text-white/50 line-through">{formatPrice(product.original_price)}</span>
            )}
          </div>

          {/* Add to cart — appears on hover */}
          <button
            onClick={handleAddToCart}
            disabled={addingToCart || product.stock === 0}
            className="opacity-0 group-hover:opacity-100 transition-all duration-300 bg-[#FEFCF9] hover:bg-[#C4A265] hover:text-white text-[#3E2723] p-2.5 rounded-full disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
            data-testid={`add-to-cart-button-${product.id}`}
            title={product.stock === 0 ? 'Sold Out' : 'Add to Cart'}
          >
            <ShoppingCart className="w-4 h-4" />
          </button>
        </div>

        {product.stock < 5 && product.stock > 0 && (
          <p className="text-[10px] text-[#C4A265] mt-2 font-medium tracking-wider uppercase">Only {product.stock} left</p>
        )}
        {product.stock === 0 && (
          <p className="text-[10px] text-red-300 mt-2 font-medium tracking-wider uppercase">Sold Out</p>
        )}
      </div>
    </Link>
  );
};

export default ProductCard;
